import type { RefObject } from 'react';
import { useEffect, useCallback, useState } from 'react';

import { SEEK_INTERVAL_IN_SECONDS } from '../../constants';

export const usePlayback = (
  videoRef: RefObject<HTMLVideoElement>,
  autoplay: boolean,
) => {
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [isWaiting, setIsWaiting] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const play = useCallback(async () => {
    try {
      await videoRef.current?.play();
    } catch {
      setIsPlaying(false);
    }
  }, [videoRef]);

  const pause = useCallback(() => {
    videoRef.current?.pause();
  }, [videoRef]);

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      return pause();
    } else {
      return play();
    }
  }, [isPlaying, pause, play]);

  const seek = useCallback(
    (newTime: number) => {
      const videoElement = videoRef.current;
      if (!videoElement) {
        return;
      }

      videoElement.currentTime = newTime;
    },
    [videoRef],
  );

  const seekForward = useCallback(() => {
    const videoElement = videoRef.current;
    if (!videoElement) {
      return;
    }

    const newTime = videoElement.currentTime + SEEK_INTERVAL_IN_SECONDS;
    const duration = videoElement.duration;

    videoElement.currentTime = Math.min(newTime, duration);
  }, [videoRef]);

  const seekBackward = useCallback(() => {
    const videoElement = videoRef.current;
    if (!videoElement) {
      return;
    }

    const newTime = Math.max(
      0,
      videoElement.currentTime - SEEK_INTERVAL_IN_SECONDS,
    );

    videoElement.currentTime = newTime;
  }, [videoRef]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) {
      return;
    }

    const handlePlayEvent = () => setIsPlaying(true);
    const handlePauseEvent = () => setIsPlaying(false);
    const handleTimeUpdateEvent = () => {
      return setCurrentTime(videoElement.currentTime);
    };
    const handleWaitingEvent = () => {
      setIsWaiting(true);
    };
    const handlePlayingEvent = () => {
      setIsWaiting(false);
      setIsPlaying(true);
    };

    videoElement.addEventListener('play', handlePlayEvent);
    videoElement.addEventListener('playing', handlePlayingEvent);
    videoElement.addEventListener('pause', handlePauseEvent);
    videoElement.addEventListener('waiting', handleWaitingEvent);
    videoElement.addEventListener('timeupdate', handleTimeUpdateEvent);

    return () => {
      videoElement.removeEventListener('play', handlePlayEvent);
      videoElement.removeEventListener('playing', handlePlayingEvent);
      videoElement.removeEventListener('pause', handlePauseEvent);
      videoElement.removeEventListener('waiting', handleWaitingEvent);
      videoElement.removeEventListener('timeupdate', handleTimeUpdateEvent);
    };
  }, [videoRef]);

  return {
    isPlaying,
    isWaiting,
    currentTime,
    play,
    pause,
    seek,
    togglePlay,
    seekForward,
    seekBackward,
  };
};
